import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI } from '../../shared/styles';
import { CLAUDE_HEADERS } from '../../shared/api';
import { MOOD_OPTIONS } from '../../shared/constants';
import SleepMoodCheckinModal from '../../modals/SleepMoodCheckinModal';
import CoachMarketplace from './CoachMarketplace';

// ── Session type definitions ────────────────────────────────────────────────
const SESSION_TYPES = [
  {
    id: 'stress',
    icon: '🧘',
    labelEN: 'Stress & Anxiety',
    labelNL: 'Stress & Angst',
    descEN: 'Identify stressors and build coping strategies',
    descNL: 'Herken stressfactoren en bouw copingstrategieën',
    color: '#7B6FD0',
    sysEN: `You are a warm CBT-informed wellness coach conducting a structured Stress & Anxiety session. Rules:
- Ask ONE focused question at a time. Never ask two questions.
- Build naturally on the user's answer. Show empathy.
- After 6 exchanges, transition to a concrete action plan with 1-2 schedulable activities.
- Keep each message under 100 words.
- End your FINAL message (after the action plan) with this exact block on a new line:
[SESSION_COMPLETE]{"summary":"2-3 sentence insight summary","actions":[{"label":"Box Breathing","icon":"🧘","duration":15,"time":"08:00","dayOffset":0}]}[/SESSION_COMPLETE]
Start: warmly open the session and ask what is weighing on the user most right now.`,
    sysNL: `Je bent een warme CBT-geïnformeerde wellness coach die een Stress & Angst sessie leidt. Regels:
- Stel ÉÉN gerichte vraag tegelijk. Nooit twee vragen.
- Bouw voort op het antwoord. Toon empathie.
- Na 6 uitwisselingen, ga naar een concreet actieplan met 1-2 inplanbare activiteiten.
- Houd elk bericht onder 100 woorden.
- Eindig je LAATSTE bericht (na het actieplan) met dit blok op een nieuwe regel:
[SESSION_COMPLETE]{"summary":"samenvatting in 2-3 zinnen","actions":[{"label":"Box Ademhaling","icon":"🧘","duration":15,"time":"08:00","dayOffset":0}]}[/SESSION_COMPLETE]
Start: open de sessie warm en vraag wat de gebruiker nu het meest bezighoudt.`,
  },
  {
    id: 'goals',
    icon: '🎯',
    labelEN: 'Goal Setting',
    labelNL: 'Doelen Stellen',
    descEN: 'Clarify your ambitions and create a concrete plan',
    descNL: 'Verduidelijk je ambities en maak een concreet plan',
    color: '#D4873A',
    sysEN: `You are a motivational coach running a structured Goal Setting session. Rules:
- Ask ONE question at a time, building toward a SMART goal.
- Explore the WHY behind goals. Challenge vague answers kindly.
- After 6 exchanges, summarise the goal and suggest 1-2 concrete first steps to schedule.
- Keep messages under 100 words.
- End your FINAL message with:
[SESSION_COMPLETE]{"summary":"the goal and why it matters","actions":[{"label":"First Step","icon":"🎯","duration":30,"time":"09:00","dayOffset":1}]}[/SESSION_COMPLETE]
Start: ask the user what goal they most want to focus on this week.`,
    sysNL: `Je bent een motiverende coach die een Doelen Stellen sessie leidt. Regels:
- Stel ÉÉN vraag tegelijk, gericht op een SMART doel.
- Verken het WAAROM achter doelen. Daag vage antwoorden vriendelijk uit.
- Na 6 uitwisselingen, vat het doel samen en stel 1-2 eerste stappen voor om in te plannen.
- Houd berichten onder 100 woorden.
- Eindig je LAATSTE bericht met:
[SESSION_COMPLETE]{"summary":"het doel en waarom het belangrijk is","actions":[{"label":"Eerste Stap","icon":"🎯","duration":30,"time":"09:00","dayOffset":1}]}[/SESSION_COMPLETE]
Start: vraag op welk doel de gebruiker zich deze week het meest wil richten.`,
  },
  {
    id: 'motivation',
    icon: '⚡',
    labelEN: 'Motivation Boost',
    labelNL: 'Motivatie Boost',
    descEN: 'Reconnect with your drive and overcome resistance',
    descNL: 'Herverbind met je drive en overwin weerstand',
    color: '#C8A84B',
    sysEN: `You are an energising coach running a Motivation Boost session. Rules:
- Ask ONE question at a time to uncover what drains and fuels the user.
- Use positive reframing. Help them find their intrinsic motivation.
- After 6 exchanges, give an action plan to rebuild momentum.
- Keep messages under 100 words.
- End your FINAL message with:
[SESSION_COMPLETE]{"summary":"what motivates them and what blocks were identified","actions":[{"label":"Energy Walk","icon":"⚡","duration":20,"time":"12:00","dayOffset":0}]}[/SESSION_COMPLETE]
Start: ask what area of life feels most draining or stuck right now.`,
    sysNL: `Je bent een energieke coach die een Motivatie Boost sessie leidt. Regels:
- Stel ÉÉN vraag tegelijk om te ontdekken wat energie geeft en neemt.
- Gebruik positief herformuleren. Help de intrinsieke motivatie vinden.
- Na 6 uitwisselingen, geef een actieplan om momentum te herwinnen.
- Houd berichten onder 100 woorden.
- Eindig je LAATSTE bericht met:
[SESSION_COMPLETE]{"summary":"wat motiveert en welke blokkades zijn gevonden","actions":[{"label":"Energiewandeling","icon":"⚡","duration":20,"time":"12:00","dayOffset":0}]}[/SESSION_COMPLETE]
Start: vraag welk levensgebied nu het meest uitputtend of vastgelopen voelt.`,
  },
  {
    id: 'sleep',
    icon: '😴',
    labelEN: 'Sleep Optimization',
    labelNL: 'Slaap Optimalisatie',
    descEN: 'Improve your sleep quality and nighttime routine',
    descNL: 'Verbeter je slaapkwaliteit en nachtelijke routine',
    color: '#5B6AD0',
    sysEN: `You are a sleep-science-informed wellness coach running a Sleep Optimization session. Rules:
- Ask ONE question at a time about sleep habits, environment, and routines.
- Give one practical tip per exchange based on answers.
- After 6 exchanges, design a personalised wind-down routine to schedule.
- Keep messages under 100 words.
- End your FINAL message with:
[SESSION_COMPLETE]{"summary":"sleep issues identified and recommended routine","actions":[{"label":"Wind-Down Routine","icon":"😴","duration":30,"time":"21:30","dayOffset":0}]}[/SESSION_COMPLETE]
Start: ask what their typical bedtime is and how they feel when they wake up.`,
    sysNL: `Je bent een slaapwetenschap-geïnformeerde coach die een Slaap Optimalisatie sessie leidt. Regels:
- Stel ÉÉN vraag tegelijk over slaapgewoonten, omgeving en routines.
- Geef per uitwisseling één praktische tip op basis van antwoorden.
- Na 6 uitwisselingen, ontwerp een persoonlijke wind-down routine om in te plannen.
- Houd berichten onder 100 woorden.
- Eindig je LAATSTE bericht met:
[SESSION_COMPLETE]{"summary":"slaapproblemen en aanbevolen routine","actions":[{"label":"Wind-Down Routine","icon":"😴","duration":30,"time":"21:30","dayOffset":0}]}[/SESSION_COMPLETE]
Start: vraag hoe laat ze normaal naar bed gaan en hoe ze zich voelen bij het wakker worden.`,
  },
  {
    id: 'growth',
    icon: '🌱',
    labelEN: 'Personal Growth',
    labelNL: 'Persoonlijke Groei',
    descEN: 'Identify limiting beliefs and unlock your potential',
    descNL: 'Herken beperkende overtuigingen en ontgrendel je potentieel',
    color: '#2A9E82',
    sysEN: `You are a growth mindset coach running a Personal Growth session. Rules:
- Ask ONE reflective question at a time to surface limiting beliefs.
- Gently challenge self-limiting thoughts. Offer reframes.
- After 6 exchanges, create 1-2 growth actions to schedule.
- Keep messages under 100 words.
- End your FINAL message with:
[SESSION_COMPLETE]{"summary":"limiting beliefs uncovered and growth direction","actions":[{"label":"Reflection Journal","icon":"🌱","duration":20,"time":"07:00","dayOffset":1}]}[/SESSION_COMPLETE]
Start: ask what area of their life they feel most held back in right now.`,
    sysNL: `Je bent een groeimindset coach die een Persoonlijke Groei sessie leidt. Regels:
- Stel ÉÉN reflectieve vraag tegelijk om beperkende overtuigingen te onthullen.
- Daag zelfbeperkende gedachten vriendelijk uit. Bied herformuleringen aan.
- Na 6 uitwisselingen, maak 1-2 groeiactiviteiten om in te plannen.
- Houd berichten onder 100 woorden.
- Eindig je LAATSTE bericht met:
[SESSION_COMPLETE]{"summary":"beperkende overtuigingen en groeirichting","actions":[{"label":"Reflectie Dagboek","icon":"🌱","duration":20,"time":"07:00","dayOffset":1}]}[/SESSION_COMPLETE]
Start: vraag op welk levensgebied ze zich nu het meest geremd voelen.`,
  },
  {
    id: 'emotional',
    icon: '❤️',
    labelEN: 'Emotional Wellbeing',
    labelNL: 'Emotioneel Welzijn',
    descEN: 'Process emotions and build emotional resilience',
    descNL: 'Verwerk emoties en bouw emotionele veerkracht',
    color: '#C06090',
    sysEN: `You are a compassionate emotional wellness coach running an Emotional Wellbeing session. Rules:
- Ask ONE gentle question at a time. Never rush emotions.
- Validate feelings before offering perspective.
- After 6 exchanges, suggest 1-2 nurturing activities to schedule.
- Keep messages under 100 words.
- End your FINAL message with:
[SESSION_COMPLETE]{"summary":"emotional themes and key insights","actions":[{"label":"Self-Care Walk","icon":"❤️","duration":30,"time":"10:00","dayOffset":0}]}[/SESSION_COMPLETE]
Start: warmly invite the user to share how they have been feeling emotionally this week.`,
    sysNL: `Je bent een medelevende emotionele wellness coach die een Emotioneel Welzijn sessie leidt. Regels:
- Stel ÉÉN zachte vraag tegelijk. Haast nooit emoties.
- Valideer gevoelens vóór het bieden van perspectief.
- Na 6 uitwisselingen, stel 1-2 zorgende activiteiten voor om in te plannen.
- Houd berichten onder 100 woorden.
- Eindig je LAATSTE bericht met:
[SESSION_COMPLETE]{"summary":"emotionele thema's en inzichten","actions":[{"label":"Zelfzorgwandeling","icon":"❤️","duration":30,"time":"10:00","dayOffset":0}]}[/SESSION_COMPLETE]
Start: nodig de gebruiker warm uit te delen hoe ze zich emotioneel hebben gevoeld deze week.`,
  },
  {
    id: 'social_sense',
    icon: '🪞',
    labelEN: 'Social Sense Analysis',
    labelNL: 'Sociale Sense Analyse',
    descEN: 'Discover how you come across to others',
    descNL: 'Ontdek hoe jij overkomt bij anderen',
    color: '#5B9ED0',
    sysEN: `You are a social intelligence coach conducting a "Social Sense Analysis" — a structured Q&A to help the user understand how they come across to others.

Rules:
- Ask exactly ONE focused question per message. Never bundle two questions.
- After each answer, write ONE short sentence acknowledging what it reveals about their social style, then immediately ask the next question.
- Ask exactly 6 questions covering these dimensions in order:
  1. Communication clarity — do people often ask them to clarify, or do they understand right away?
  2. Listening vs. advising — when someone shares a problem, do they listen first or immediately give advice?
  3. Emotional availability — are they emotionally present, or do they tend to stay rational/distant in conversations?
  4. Self-disclosure — how much do they typically share about themselves: a lot, selectively, or very little?
  5. Consistency — do the people around them know what to expect from them emotionally and behaviourally?
  6. Repair after misunderstanding — when there's a miscommunication, who usually initiates the repair?
- After 6 answers, write a personalised "Social Sense Analysis":
  • Identify 2 strengths and 1-2 specific growth areas with brief science-backed explanations (cite Lopes 2005, Cialdini 2006, or Pennebaker 1997 naturally).
  • If 2+ growth areas are identified (low social sense), prescribe 2-3 specific Sense Lifting exercises to train: choose from Body Scan, Conscious Listening, Emotion Check-in, Reflection after Conversation, Focus Sync, or Gratitude Practice — and briefly explain WHY each exercise trains that specific gap.
  • If mostly strengths, prescribe 1 reinforcement exercise.
  • Keep tone warm, not clinical. Under 160 words total for the analysis.
- End your FINAL message with:
[SESSION_COMPLETE]{"summary":"2-3 sentence personal insight about how they come across and what to develop","actions":[{"label":"Conscious Listening","icon":"🪞","duration":15,"time":"19:00","dayOffset":1},{"label":"Emotion Check-in","icon":"❤️","duration":10,"time":"08:00","dayOffset":0}]}[/SESSION_COMPLETE]
(Adjust the actions array to match the exercises you actually prescribed — include 1 to 3 actions.)

Start: in 1 sentence explain this session reveals patterns in how they come across to others — then ask question 1.`,
    sysNL: `Je bent een sociale intelligentie coach die een "Sociale Sense Analyse" uitvoert — een gestructureerd vraaggesprek om de gebruiker te helpen begrijpen hoe ze overkomen bij anderen.

Regels:
- Stel per bericht precies ÉÉN gerichte vraag. Nooit twee vragen tegelijk.
- Na elk antwoord, schrijf ÉÉN korte zin die aangeeft wat het onthult over hun sociale stijl, daarna direct de volgende vraag.
- Stel precies 6 vragen, in deze volgorde:
  1. Communicatiehelderheid — vragen mensen hen vaak om verduidelijking, of begrijpen ze het meteen?
  2. Luisteren vs. adviseren — als iemand een probleem deelt, luisteren ze eerst of geven ze meteen advies?
  3. Emotionele beschikbaarheid — zijn ze emotioneel aanwezig, of blijven ze rationeel/afstandelijk in gesprekken?
  4. Zelfopenbaring — hoeveel delen ze normaal over zichzelf: veel, selectief of heel weinig?
  5. Consistentie — weten de mensen om hen heen wat ze emotioneel en gedragsmatig van hen kunnen verwachten?
  6. Herstel na misverstand — wie initieert het herstel als er miscommunicatie is?
- Na 6 antwoorden, schrijf een persoonlijke "Sociale Sense Analyse":
  • Identificeer 2 sterke punten en 1-2 specifieke groeipunten met korte wetenschappelijk onderbouwde verklaringen (citeer Lopes 2005, Cialdini 2006 of Pennebaker 1997 op een natuurlijke manier).
  • Als er 2+ groeipunten zijn (lage sociale sense), schrijf 2-3 specifieke Sense Lifting oefeningen voor: kies uit Lichaamsscan, Bewust Luisteren, Emotionele Check-in, Reflectie na Gesprek, Focus Sync of Dankbaarheidsoefening — en leg kort uit WAAROM elke oefening dat specifieke groeipunt traint.
  • Als er vooral sterke punten zijn, schrijf 1 versterkingsoefening voor.
  • Warm van toon, niet klinisch. Maximaal 160 woorden voor de analyse.
- Eindig je LAATSTE bericht met:
[SESSION_COMPLETE]{"summary":"persoonlijk inzicht in 2-3 zinnen over hoe ze overkomen en wat te ontwikkelen","actions":[{"label":"Bewust Luisteren","icon":"🪞","duration":15,"time":"19:00","dayOffset":1},{"label":"Emotionele Check-in","icon":"❤️","duration":10,"time":"08:00","dayOffset":0}]}[/SESSION_COMPLETE]
(Pas de actions array aan op de oefeningen die je daadwerkelijk hebt voorgeschreven — 1 tot 3 acties.)

Start: leg in 1 zin uit dat deze sessie patronen onthult in hoe ze overkomen — stel dan vraag 1.`,
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────
function parseSessionComplete(text) {
  const m = text.match(/\[SESSION_COMPLETE\]([\s\S]*?)\[\/SESSION_COMPLETE\]/);
  if (!m) return null;
  try { return JSON.parse(m[1].trim()); } catch { return null; }
}

function stripMarker(text) {
  return text.replace(/\[SESSION_COMPLETE\][\s\S]*?\[\/SESSION_COMPLETE\]/, '').trim();
}

function loadSessions() {
  try { return JSON.parse(localStorage.getItem('sl_coach_sessions') || '[]'); } catch { return []; }
}

function saveSessions(sessions) {
  localStorage.setItem('sl_coach_sessions', JSON.stringify(sessions));
}

// ── Component ────────────────────────────────────────────────────────────────
function CoachScreen({ lang, weekIntention, weekAgenda, setWeekAgenda, currentUserId }) {
  const nl = lang === 'nl';
  const [activeView, setActiveView] = useState('ai'); // 'ai' | 'marketplace'
  const todayKey     = new Date().toISOString().slice(0, 10);
  const yesterdayKey = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  // ── Detect missing sleep ──────────────────────────────────────────
  const missingFor = (() => {
    try {
      const sleepLog = JSON.parse(localStorage.getItem('sl_sleep_log') || '[]');
      if (!sleepLog.some(e => e.date === todayKey))     return todayKey;
      if (!sleepLog.some(e => e.date === yesterdayKey)) return yesterdayKey;
    } catch {}
    return null;
  })();

  // ── Context for Claude ────────────────────────────────────────────
  const contextSummary = (() => {
    try {
      const sleepLog  = JSON.parse(localStorage.getItem('sl_sleep_log')  || '[]');
      const moodLog   = JSON.parse(localStorage.getItem('sl_mood_log')   || '[]');
      const exHistory = JSON.parse(localStorage.getItem('sl_ex_history') || '[]');
      const parts = [];
      const s = sleepLog.find(e => e.date === todayKey) || sleepLog.find(e => e.date === yesterdayKey);
      if (s) parts.push(nl ? `Slaap: ${s.hours ?? Math.round((s.durationMins||0)/60)}h, score ${s.score}/10` : `Sleep: ${s.hours ?? Math.round((s.durationMins||0)/60)}h, score ${s.score}/10`);
      const m = moodLog.find(e => e.date === todayKey) || moodLog.find(e => e.date === yesterdayKey);
      if (m) { const opt = MOOD_OPTIONS.find(o => o.id === m.mood); parts.push(nl ? `Stemming: ${opt?.label ?? m.mood}` : `Mood: ${opt?.labelEN ?? m.mood}`); }
      const weekAgo = Date.now() - 7 * 86400000;
      const recentEx = exHistory.filter(e => e.date > weekAgo);
      if (recentEx.length) parts.push(nl ? `Oefeningen: ${recentEx.length}x` : `Exercises: ${recentEx.length}x`);
      return parts.length ? parts.join(' | ') : null;
    } catch { return null; }
  })();

  // ── State ─────────────────────────────────────────────────────────
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinFor,  setCheckinFor]  = useState('today');
  const [activeSessionType, setActiveSessionType] = useState(null); // SESSION_TYPES entry
  const [sessionComplete,   setSessionComplete]   = useState(null); // parsed JSON
  const [scheduledActions,  setScheduledActions]  = useState([]);   // actions user confirmed
  const [sessionSaved,      setSessionSaved]       = useState(false);

  // Free-chat messages (no session active)
  const [msgs, setMsgs] = useState(() => {
    const greet = nl
      ? `Hoi! Ik ben jouw Sense Lifting coach 🦋\n\nWeekintentie: "${weekIntention || 'nog niet ingesteld'}"${contextSummary ? `\n${contextSummary}` : ''}\n\nKies een sessie hieronder of stel me gewoon een vraag.`
      : `Hi! I'm your Sense Lifting coach 🦋\n\nWeekly intention: "${weekIntention || 'not set yet'}"${contextSummary ? `\n${contextSummary}` : ''}\n\nPick a session below or just ask me anything.`;
    return [{ role: 'assistant', content: greet }];
  });

  // Session messages
  const [sessionMsgs, setSessionMsgs] = useState([]);
  const [input,   setInput]   = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const activeMsgs    = activeSessionType ? sessionMsgs : msgs;
  const setActiveMsgs = activeSessionType ? setSessionMsgs : setMsgs;

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [activeMsgs, loading]);

  // ── Auto-prompt if sleep missing ──────────────────────────────────
  useEffect(() => {
    if (!missingFor || activeSessionType) return;
    const timer = setTimeout(() => {
      const label = missingFor === yesterdayKey ? (nl ? 'gisteren' : 'yesterday') : (nl ? 'vandaag' : 'today');
      const prompt = nl
        ? `Ik zie dat je slaap van ${label} nog niet is ingevuld. Wil je dat nu snel doen? 👇`
        : `I notice your sleep for ${label} hasn't been logged yet. Want to fill it in quickly? 👇`;
      setMsgs(m => [...m, { role: 'assistant', content: prompt, action: 'checkin', forDate: missingFor }]);
    }, 800);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Start a session ───────────────────────────────────────────────
  const startSession = async (sType) => {
    setActiveSessionType(sType);
    setSessionComplete(null);
    setScheduledActions([]);
    setSessionSaved(false);
    setSessionMsgs([]);
    setInput('');
    // Kick off first AI message
    setLoading(true);
    try {
      const sys = (nl ? sType.sysNL : sType.sysEN) + (contextSummary ? `\n\nUser context: ${contextSummary}` : '');
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST', headers: CLAUDE_HEADERS,
        body: JSON.stringify({ model: 'claude-haiku-4-5-20251001', max_tokens: 300, system: sys, messages: [{ role: 'user', content: nl ? 'Start de sessie.' : 'Start the session.' }] }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || '';
      setSessionMsgs([{ role: 'assistant', content: stripMarker(text) }]);
    } catch {
      setSessionMsgs([{ role: 'assistant', content: nl ? 'Er ging iets mis. Probeer opnieuw.' : 'Something went wrong. Try again.' }]);
    }
    setLoading(false);
  };

  // ── Send message ──────────────────────────────────────────────────
  const send = async (override) => {
    const txt = override || input.trim();
    if (!txt || loading) return;
    setInput('');
    const um = { role: 'user', content: txt };
    setActiveMsgs(m => [...m, um]);
    setLoading(true);
    try {
      let sys, history;
      if (activeSessionType) {
        sys = (nl ? activeSessionType.sysNL : activeSessionType.sysEN) + (contextSummary ? `\n\nUser context: ${contextSummary}` : '');
        history = [...sessionMsgs.filter(m => !m.action).map(m => ({ role: m.role, content: m.content })), um];
      } else {
        sys = nl
          ? `Je bent een warme, persoonlijke wellness coach bij Sense Lifting. Antwoord altijd in het Nederlands. Wees beknopt (max 150 woorden), concreet en motiverend.\n\nWeekintentie: "${weekIntention || 'niet ingesteld'}"\n${contextSummary ? `Gebruikersdata: ${contextSummary}` : ''}`
          : `You are a warm, personal wellness coach at Sense Lifting. Always reply in English. Be concise (max 150 words), concrete and motivating.\n\nWeekly intention: "${weekIntention || 'not set'}"\n${contextSummary ? `User data: ${contextSummary}` : ''}`;
        history = [...msgs.filter(m => !m.action).map(m => ({ role: m.role, content: m.content })), um];
      }
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST', headers: CLAUDE_HEADERS,
        body: JSON.stringify({ model: 'claude-haiku-4-5-20251001', max_tokens: 300, system: sys, messages: history }),
      });
      const data = await res.json();
      const rawText = data.content?.[0]?.text || (nl ? 'Probeer opnieuw.' : 'Try again.');
      const parsed  = parseSessionComplete(rawText);
      const display = stripMarker(rawText);
      setActiveMsgs(m => [...m, { role: 'assistant', content: display }]);
      if (parsed && activeSessionType) setSessionComplete(parsed);
    } catch {
      setActiveMsgs(m => [...m, { role: 'assistant', content: nl ? 'Probeer opnieuw.' : 'Try again.' }]);
    }
    setLoading(false);
  };

  // ── Schedule an action to agenda ──────────────────────────────────
  const scheduleAction = (action) => {
    if (!setWeekAgenda || !weekAgenda) return;
    const todayDow = new Date().getDay(); // 0=Sun
    const todayIdx = todayDow === 0 ? 6 : todayDow - 1; // 0=Mon
    const targetIdx = Math.min(6, todayIdx + (action.dayOffset || 0));
    const item = {
      id: Date.now() + Math.random(),
      type: 'custom',
      icon: action.icon || '📌',
      label: action.label,
      labelNL: action.label,
      startTime: action.time || '09:00',
      endTime: (() => {
        const [h, m2] = (action.time || '09:00').split(':').map(Number);
        const end = new Date(0, 0, 0, h, m2 + (action.duration || 30));
        return `${String(end.getHours()).padStart(2,'0')}:${String(end.getMinutes()).padStart(2,'0')}`;
      })(),
      duration: action.duration || 30,
      mental: 30, physical: 10,
    };
    setWeekAgenda(prev => ({ ...prev, [targetIdx]: [...(prev[targetIdx] || []), item] }));
    setScheduledActions(s => [...s, action.label]);
  };

  // ── Save session ──────────────────────────────────────────────────
  const saveSession = () => {
    if (!activeSessionType || !sessionComplete) return;
    const sessions = loadSessions();
    sessions.unshift({
      id: Date.now(),
      type: activeSessionType.id,
      icon: activeSessionType.icon,
      labelEN: activeSessionType.labelEN,
      labelNL: activeSessionType.labelNL,
      color: activeSessionType.color,
      date: todayKey,
      summary: sessionComplete.summary || '',
      actions: sessionComplete.actions || [],
      scheduled: scheduledActions,
    });
    saveSessions(sessions.slice(0, 50)); // keep last 50
    setSessionSaved(true);
  };

  // ── Quick prompts (free chat) ─────────────────────────────────────
  const guided = [
    { icon: '💨', label: 'Breathing', p: nl ? 'Begeleid me door 4-4-6-2 ademhaling.' : 'Guide me through 4-4-6-2 breathing.' },
    { icon: '⏱️', label: 'Focus',     p: nl ? 'Help me focussessie starten.'         : 'Help me start a focus session.' },
    { icon: '😤', label: 'Stress',    p: nl ? 'Ik voel me gestrest.'                  : "I'm feeling stressed." },
    { icon: '🌙', label: 'Wind down', p: nl ? 'Begeleid me door dag-afsluiting.'       : 'Guide me through a wind-down.' },
  ];

  const handleCheckinSaved = ({ hours, quality, sleepScore, mood }) => {
    const opt = MOOD_OPTIONS.find(m => m.id === mood);
    const moodLabel = opt ? (nl ? opt.label : opt.labelEN) : '';
    setMsgs(m => [...m, { role: 'assistant', content: nl
      ? `Super gedaan! 🌟 Slaap: ${hours}h · Score ${sleepScore}/10\n💭 Stemming: ${moodLabel || '—'}`
      : `Well done! 🌟 Sleep: ${hours}h · Score ${sleepScore}/10\n💭 Mood: ${moodLabel || '—'}` }]);
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <>
      {showCheckin && (
        <SleepMoodCheckinModal lang={lang} forDate={checkinFor} onClose={() => setShowCheckin(false)} onSaved={handleCheckinSaved} />
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `1px solid ${_T.cardBorderBot}` }}>
        {[
          { id: 'ai',          icon: '🦋', label: nl ? 'AI Coach' : 'AI Coach' },
          { id: 'marketplace', icon: '🔍', label: nl ? 'Coach inhuren' : 'Find a Coach' },
        ].map(tab => {
          const active = activeView === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveView(tab.id)} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '10px 0', border: 'none', background: 'none',
              borderBottom: `2.5px solid ${active ? _T.accent : 'transparent'}`,
              color: active ? _T.accent : C.silver, fontSize: 12, fontWeight: active ? 700 : 400,
              cursor: 'pointer', transition: 'all 0.2s',
            }}>
              <span style={{ fontSize: 15 }}>{tab.icon}</span>
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeView === 'marketplace' && (
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <CoachMarketplace lang={lang} currentUserId={currentUserId} />
        </div>
      )}

      {activeView === 'ai' && (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>

        {/* Header */}
        {activeSessionType ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <button onClick={() => { setActiveSessionType(null); setSessionComplete(null); }} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 20, padding: '2px 4px' }}>←</button>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: `${activeSessionType.color}22`, border: `1px solid ${activeSessionType.color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{activeSessionType.icon}</div>
            <div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 15, color: C.white }}>{nl ? activeSessionType.labelNL : activeSessionType.labelEN}</div>
              <div style={{ fontSize: 10, color: activeSessionType.color, fontWeight: 600, letterSpacing: 1 }}>{nl ? 'SESSIE ACTIEF' : 'SESSION ACTIVE'}</div>
            </div>
          </div>
        ) : (
          <>
            {/* Session picker */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 600, marginBottom: 6 }}>{nl ? 'SESSIES' : 'SESSIONS'}</div>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                {SESSION_TYPES.map(st => (
                  <button key={st.id} onClick={() => startSession(st)}
                    style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: `${st.color}14`, border: `1px solid ${st.color}44`, borderRadius: 12, padding: '8px 10px', cursor: 'pointer', minWidth: 72 }}>
                    <span style={{ fontSize: 18 }}>{st.icon}</span>
                    <span style={{ fontSize: 9, color: st.color, fontWeight: 700, textAlign: 'center', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{nl ? st.labelNL : st.labelEN}</span>
                  </button>
                ))}
              </div>
            </div>
            {/* Quick prompts */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {guided.map(g => (
                <button key={g.label} onClick={() => send(g.p)}
                  style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderTop}`, color: C.silver2, borderRadius: 10, padding: '7px 10px', cursor: 'pointer', fontSize: 11 }}>
                  {g.icon} {g.label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
          {activeMsgs.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '82%',
                background: m.role === 'user' ? `${_T.accent}18` : _T.bg3,
                border: `1px solid ${m.role === 'user' ? _T.cardBorderBot : (activeSessionType ? `${activeSessionType.color}33` : _T.cardBorderBot)}`,
                borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                padding: '12px 16px', fontSize: 13, color: C.silver2, lineHeight: 1.7, whiteSpace: 'pre-wrap',
              }}>
                {m.role === 'assistant' && <div style={{ fontSize: 16, marginBottom: 4 }}>{activeSessionType ? activeSessionType.icon : '🦋'}</div>}
                {m.content}
                {m.action === 'checkin' && (
                  <button onClick={() => { setCheckinFor(m.forDate || todayKey); setShowCheckin(true); }}
                    style={{ display: 'block', marginTop: 10, background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, padding: '8px 14px', color: _T.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {nl ? '📝 Slaap & stemming invullen' : '📝 Log sleep & mood'}
                  </button>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ background: _T.bg3, borderRadius: '16px 16px 16px 4px', padding: '12px 16px', color: C.silver, fontSize: 13 }}>
                {activeSessionType ? activeSessionType.icon : '🦋'} ···
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Session complete card */}
        {sessionComplete && activeSessionType && (
          <div style={{ background: `${activeSessionType.color}12`, border: `1px solid ${activeSessionType.color}44`, borderRadius: 16, padding: 14, marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: activeSessionType.color, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
              ✓ {nl ? 'SESSIE VOLTOOID' : 'SESSION COMPLETE'}
            </div>
            <div style={{ fontSize: 12, color: C.silver2, lineHeight: 1.5, marginBottom: 10 }}>{sessionComplete.summary}</div>
            {/* Schedulable actions */}
            {(sessionComplete.actions || []).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: C.silver, letterSpacing: 1, marginBottom: 6 }}>{nl ? 'INPLANNEN IN AGENDA:' : 'ADD TO AGENDA:'}</div>
                {sessionComplete.actions.map((a, i) => {
                  const done = scheduledActions.includes(a.label);
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, padding: '6px 10px' }}>
                        <span style={{ fontSize: 14 }}>{a.icon || '📌'}</span>
                        <div>
                          <div style={{ fontSize: 11, color: C.white, fontWeight: 600 }}>{a.label}</div>
                          <div style={{ fontSize: 10, color: C.silver }}>{a.time} · {a.duration}min · {a.dayOffset === 0 ? (nl ? 'vandaag' : 'today') : a.dayOffset === 1 ? (nl ? 'morgen' : 'tomorrow') : `+${a.dayOffset}d`}</div>
                        </div>
                      </div>
                      {done
                        ? <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>✓</span>
                        : <button onClick={() => scheduleAction(a)} style={s_GB({ fontSize: 10, padding: '5px 10px', borderRadius: 8, flexShrink: 0 })}>{nl ? 'Inplannen' : 'Schedule'}</button>
                      }
                    </div>
                  );
                })}
              </div>
            )}
            {/* Save button */}
            {sessionSaved
              ? <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>✓ {nl ? 'Sessie opgeslagen in je profiel' : 'Session saved to your profile'}</div>
              : <button onClick={saveSession} style={s_PB({ width: '100%', fontSize: 12 })}>💾 {nl ? 'Sla sessie op' : 'Save session'}</button>
            }
          </div>
        )}

        {/* Input */}
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            placeholder={activeSessionType ? (nl ? 'Antwoord...' : 'Reply...') : (nl ? 'Schrijf een bericht...' : 'Write a message...')}
            style={s_TI({ flex: 1, borderRadius: 14 })}
          />
          <button style={s_PB({ borderRadius: 14, padding: '12px 18px' })} onClick={() => send()} disabled={loading}>→</button>
        </div>
      </div>
      )}
    </>
  );
}

export default CoachScreen;
